import { buildTranslate } from 'locales'
import isNil from 'lodash/isNil'
import { ChannelData } from 'models/CaptureRateMetric'
import moment from 'moment-timezone'
import React from 'react'
import BodyCell from 'scenes/Reports/components/Table/BodyCell'
import { getFormattedDate } from 'utilities/date'
import { currencyFormatter, percentageFormatter } from 'utilities/formatters'

const t = buildTranslate('revenue.tabs.revenue.popovers')

type Props = {
  data: Partial<ChannelData>
  isErrored: boolean
  isLoading: boolean
}

const Row: React.FC<Props> = props => {
  const { data, isErrored, isLoading } = props
  const timeZone = moment.tz.guess()

  if (!data) {
    return null
  }
  const displayDateTime = getFormattedDate(
    moment.tz(data.date_time, timeZone),
    'monthly'
  )

  const displayPercent = (value?: number) => {
    if (isNil(value)) return '-'
    return percentageFormatter(value / 100)
  }

  const displayAmount = (value?: number) => {
    if (isNil(value)) return '-'
    return currencyFormatter(value, false)
  }

  const totalAmount = data.member_purchases_total_amount
  const instoreAmount = data.member_purchases_instore_amount
  const digitalAmount = data.member_purchases_digital_amount

  return (
    <tr>
      <BodyCell
        isErrored={isErrored}
        isLoading={isLoading}
        values={[{ value: displayDateTime }]}
      />
      <BodyCell
        className="text-right"
        isErrored={isErrored}
        isLoading={isLoading}
        values={[
          {
            value: displayPercent(
              totalAmount && instoreAmount
                ? (100 * instoreAmount) / totalAmount
                : undefined
            ),
            sections: [
              {
                content: t('revenue_by_channel.in_store.percent.section_1'),
              },
              {
                title: t('how_its_calculated'),
                content: t('revenue_by_channel.in_store.percent.section_2'),
              },
              {
                title: t('example'),
                content: t('revenue_by_channel.in_store.percent.section_3'),
              },
            ],
          },
          {
            value: displayAmount(data.member_purchases_instore_amount),
            sections: [
              {
                content: t('revenue_by_channel.in_store.amount.section_1'),
              },
              {
                title: t('how_its_calculated'),
                content: t('revenue_by_channel.in_store.amount.section_2'),
              },
              {
                title: t('example'),
                content: t('revenue_by_channel.in_store.amount.section_3'),
              },
            ],
          },
        ]}
      />
      <BodyCell
        className="text-right"
        isErrored={isErrored}
        isLoading={isLoading}
        values={[
          {
            value: displayPercent(
              totalAmount && digitalAmount
                ? (100 * digitalAmount) / totalAmount
                : undefined
            ),
            sections: [
              {
                content: t('revenue_by_channel.digital.percent.section_1'),
              },
              {
                title: t('how_its_calculated'),
                content: t('revenue_by_channel.digital.percent.section_2'),
              },
              {
                title: t('example'),
                content: t('revenue_by_channel.digital.percent.section_3'),
              },
            ],
          },
          {
            value: displayAmount(data.member_purchases_digital_amount),
            sections: [
              {
                content: t('revenue_by_channel.digital.amount.section_1'),
              },
              {
                title: t('how_its_calculated'),
                content: t('revenue_by_channel.digital.amount.section_2'),
              },
              {
                title: t('example'),
                content: t('revenue_by_channel.digital.amount.section_3'),
              },
            ],
          },
        ]}
      />
      <BodyCell
        className="text-right"
        isErrored={isErrored}
        isLoading={isLoading}
        values={[
          {
            value: displayAmount(totalAmount),
            sections: [
              {
                content: t('revenue_by_channel.total.section_1'),
              },
              {
                title: t('how_its_calculated'),
                content: t('revenue_by_channel.total.section_2'),
              },
              {
                title: t('example'),
                content: t('revenue_by_channel.total.section_3'),
              },
            ],
          },
        ]}
      />
    </tr>
  )
}

export default Row
